<template>
  <div class="work-energy-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΔE = W_ext + W_non_cons$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <!-- 核心物理场 -->
        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <marker id="arr-force" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#38bdf8" /></marker>
          </defs>

          <!-- 约束轨道 (刚性检测区) -->
          <rect x="20" y="160" width="410" height="4" fill="#1e293b" rx="2" />
          <path d="M 20,162 H 300 L 400,102" fill="none" stroke="#334155" stroke-width="2" stroke-dasharray="4,4" />

          <!-- 能量条 (实时关联) -->
          <g transform="translate(30, 40)">
             <rect width="180" height="12" fill="rgba(15,23,42,0.8)" rx="6" />
             <rect :width="Math.min(180, (ek + ep)/10)" height="12" fill="#10b981" rx="6" opacity="0.6" />
             <text x="5" y="-8" fill="#94a3b8" font-size="9" font-weight="900">系统总机械能 E_total</text>
          </g>

          <!-- 物理实体 (Object) -->
          <g :transform="`translate(${objX}, ${objY}) rotate(${objRot})`">
             <rect x="-15" y="-15" width="30" height="20" fill="#0ea5e9" rx="4" />
             <line x1="15" y1="-5" x2="45" y2="-5" stroke="#38bdf8" stroke-width="3" marker-end="url(#arr-force)" v-if="isRunning" />
             <text y="-20" text-anchor="middle" fill="#fff" font-size="8">m</text>
          </g>

          <!-- 比例尺 -->
          <g transform="translate(0, 180)" fill="#334155" font-size="8">
             <text x="20" y="0">0m</text><text x="120" y="0">10m</text><text x="220" y="0">20m</text><text x="320" y="0">30m</text>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan"><label>外力功 W_ext</label><span class="val">{{ wExt.toFixed(1) }} J</span></div>
           <div class="hud-item orange"><label>摩擦功 W_f</label><span class="val">{{ wFriction.toFixed(1) }} J</span></div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">外力 F_ext (N)</label>
                 <el-slider :min="0" :max="50" v-model="f_ext" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">动摩擦因数 μ</label>
                 <el-slider :min="0" :max="1" :step="0.01" v-model="mu" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="startSim" :disabled="isRunning">开始推行</el-button>
                 <el-button size="small" @click="resetSim">重置</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>严谨物理逻辑：</strong>非保守力（推力 F 和摩擦力 f）所做的功之和，恰好等于系统<strong>机械能的增量</strong>。观察：当你加大 μ 值，系统能量由于热耗散显著降低。<strong>刚性边界已锁定：</strong>无论如何调参，物体均会停顿在 400px 测量极值处，不会飞出视图。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'WorkEnergyPrincipleViz',
  data() {
    return {
      f_ext: 25,
      mu: 0.1,
      objX: 20,
      objY: 158,
      objRot: 0,
      ek: 0,
      ep: 0,
      wExt: 0,
      wFriction: 0,
      isRunning: false
    }
  },
  methods: {
    startSim() {
       this.isRunning = true;
       this.animate();
    },
    resetSim() {
       this.isRunning = false;
       cancelAnimationFrame(this.animId);
       this.objX = 20;
       this.objY = 158;
       this.objRot = 0;
       this.ek = 0;
       this.ep = 0;
       this.wExt = 0;
       this.wFriction = 0;
    },
    animate() {
       if (!this.isRunning) return;
       // Displacement step
       const dx = 2.5;
       
       // Physics Logic: Apply constraints
       if (this.objX < 400) {
          const prevX = this.objX;
          this.objX += dx;
          
          // Path handling (Hill logic)
          if (this.objX > 300) {
             const progress = (this.objX - 300) / 100;
             this.objY = 158 - progress * 60;
             this.objRot = -31;
             this.ep = progress * 1500; // Potential energy gain
          }

          // Work calculation (Delta W = F * dx)
          this.wExt += this.f_ext * dx * 0.1;
          this.wFriction -= this.mu * 10 * dx * 0.1; // mg * mu * dx
          this.ek = Math.max(0, this.wExt + this.wFriction - this.ep);
       } else {
          this.isRunning = false;
       }
       
       this.animId = requestAnimationFrame(this.animate);
    }
  },
  beforeDestroy() { cancelAnimationFrame(this.animId); }
}
</script>

<style scoped lang="scss">
.work-energy-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #f97316; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 20px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } .btn-group { display: flex; gap: 8px; } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
